import moment from 'moment';
import { create } from 'zustand';
import { HospitalityReservationResponseDTO } from '@/services/hospitality.service';
import { WaveLevelEnum } from '@/helpers/utils';
import { CartServiceType, type CartValuesServiceStore } from '../shoppingCart/shoppingCartStore';

type FreeWavesByDay = {
    dateKey: string;
    count: number;
};

export type ReservationDailyWaveUsage = {
    dateKey: string;
    freeReef: number;
    freeBay: number;
    paidReef: number;
    paidBay: number;
};

type ActiveReservation = {
    id: number;
    token: string;
    checkIn: Date;
    checkOut: Date;
    freeWavesByDay: FreeWavesByDay[];
    dailyWaveUsage: ReservationDailyWaveUsage[];
};

type State = {
    activeReservation: ActiveReservation | null;
};

type Action = {
    setActiveReservation: (reservation: HospitalityReservationResponseDTO) => void;
    clearActiveReservation: () => void;
};

const DAILY_FREE_REEF_LIMIT = 1;
const DAILY_FREE_BAY_LIMIT = 1;
const DAILY_PAID_REEF_LIMIT = 1;

const getDateKey = (dateInput: Date | string) => moment(dateInput).format('YYYY-MM-DD');

const resolveWaveLevelByName = (name: string): WaveLevelEnum => {
    if (name.toLowerCase().includes('bay')) return WaveLevelEnum.BAY;
    return WaveLevelEnum.REEF;
};

const createEmptyUsage = (dateKey: string): ReservationDailyWaveUsage => ({
    dateKey,
    freeReef: 0,
    freeBay: 0,
    paidReef: 0,
    paidBay: 0,
});

const mapToSortedUsage = (
    dayMap: Map<string, ReservationDailyWaveUsage>
): ReservationDailyWaveUsage[] =>
    Array.from(dayMap.values()).sort((a, b) => a.dateKey.localeCompare(b.dateKey));

export const getReservationDailyWaveUsage = (
    reservation: HospitalityReservationResponseDTO
): ReservationDailyWaveUsage[] => {
    const dayMap = reservation.products.reduce((acc, product) => {
        const dateKey = getDateKey(product.date);
        const current = acc.get(dateKey) || createEmptyUsage(dateKey);
        const waveLevel = resolveWaveLevelByName(product.name);

        const isFreeWave = (product.value ?? 0) <= 0;

        if (waveLevel === WaveLevelEnum.REEF) {
            if (isFreeWave) current.freeReef += 1;
            else current.paidReef += 1;
        } else if (waveLevel === WaveLevelEnum.BAY) {
            if (isFreeWave) current.freeBay += 1;
            else current.paidBay += 1;
        }

        acc.set(dateKey, current);
        return acc;
    }, new Map<string, ReservationDailyWaveUsage>());

    return mapToSortedUsage(dayMap);
};

export const getCartDailyWaveUsage = (
    cartValues: CartValuesServiceStore[]
): ReservationDailyWaveUsage[] => {
    const dayMap = cartValues.reduce((acc, item) => {
        if (item.type !== CartServiceType.Waves) return acc;

        const dateKey = getDateKey(item.date);
        const current = acc.get(dateKey) || createEmptyUsage(dateKey);
        const waveLevel = item.waveLevel || resolveWaveLevelByName(item.title);
        const isFreeWave =
            typeof item.giftId === 'number' ||
            item.freeSession === true ||
            item.value <= 0;

        if (waveLevel === WaveLevelEnum.REEF) {
            if (isFreeWave) current.freeReef += 1;
            else current.paidReef += 1;
        } else if (waveLevel === WaveLevelEnum.BAY) {
            if (isFreeWave) current.freeBay += 1;
            else current.paidBay += 1;
        }

        acc.set(dateKey, current);
        return acc;
    }, new Map<string, ReservationDailyWaveUsage>());

    return mapToSortedUsage(dayMap);
};

export const mergeDailyWaveUsages = (
    reservationUsage: ReservationDailyWaveUsage[],
    cartUsage: ReservationDailyWaveUsage[]
): ReservationDailyWaveUsage[] => {
    const mergedMap = new Map<string, ReservationDailyWaveUsage>();

    [...reservationUsage, ...cartUsage].forEach(item => {
        const current = mergedMap.get(item.dateKey) || createEmptyUsage(item.dateKey);
        current.freeReef += item.freeReef;
        current.freeBay += item.freeBay;
        current.paidReef += item.paidReef;
        current.paidBay += item.paidBay;
        mergedMap.set(item.dateKey, current);
    });

    return mapToSortedUsage(mergedMap);
};

export type DailyWaveLimitViolationType =
    | 'Reservation.DailyFreeReefLimitReached'
    | 'Reservation.DailyFreeBayLimitReached'
    | 'Reservation.DailyPaidReefLimitReached'
    | 'Reservation.PaidBayNotAllowed';

export type ReservationDayTypeStatus = 'available' | 'exhausted';

export type ReservationDayEligibilityStatus = {
    dateKey: string;
    freeReefStatus: ReservationDayTypeStatus;
    freeBayStatus: ReservationDayTypeStatus;
    paidReefStatus: ReservationDayTypeStatus;
};

export const findDailyWaveLimitViolation = (
    mergedUsage: ReservationDailyWaveUsage[]
): DailyWaveLimitViolationType | null => {
    for (const usage of mergedUsage) {
        if (usage.freeReef > DAILY_FREE_REEF_LIMIT) {
            return 'Reservation.DailyFreeReefLimitReached';
        }
        if (usage.freeBay > DAILY_FREE_BAY_LIMIT) {
            return 'Reservation.DailyFreeBayLimitReached';
        }
        if (usage.paidReef > DAILY_PAID_REEF_LIMIT) {
            return 'Reservation.DailyPaidReefLimitReached';
        }
        if (usage.paidBay > 0) {
            return 'Reservation.PaidBayNotAllowed';
        }
    }

    return null;
};

export const buildReservationDayEligibilityStatuses = (
    mergedUsage: ReservationDailyWaveUsage[]
): ReservationDayEligibilityStatus[] =>
    mergedUsage.map(dayUsage => ({
        dateKey: dayUsage.dateKey,
        freeReefStatus:
            dayUsage.freeReef >= DAILY_FREE_REEF_LIMIT
                ? 'exhausted'
                : 'available',
        freeBayStatus:
            dayUsage.freeBay >= DAILY_FREE_BAY_LIMIT ? 'exhausted' : 'available',
        paidReefStatus:
            dayUsage.paidReef >= DAILY_PAID_REEF_LIMIT
                ? 'exhausted'
                : 'available',
    }));

export const useReservationStore = create<State & Action>(set => ({
    activeReservation: null,
    setActiveReservation: reservation => {
        const dayMap = reservation.products.reduce((acc, product) => {
            const dateKey = moment(product.date).format('YYYY-MM-DD');
            acc.set(dateKey, (acc.get(dateKey) || 0) + 1);
            return acc;
        }, new Map<string, number>());

        const freeWavesByDay: FreeWavesByDay[] = Array.from(dayMap.entries())
            .map(([dateKey, count]) => ({ dateKey, count }))
            .sort((a, b) => a.dateKey.localeCompare(b.dateKey));

        const dailyWaveUsage = getReservationDailyWaveUsage(reservation);

        set({
            activeReservation: {
                id: reservation.id,
                token: reservation.token,
                checkIn: new Date(reservation.checkIn),
                checkOut: new Date(reservation.checkOut),
                freeWavesByDay,
                dailyWaveUsage,
            },
        });
    },
    clearActiveReservation: () => {
        set({ activeReservation: null });
    },
}));
